import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/brand";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

export default function Diagnose() {
  const [machine, setMachine] = useState("");
  const [fabric, setFabric] = useState("");
  const [thread, setThread] = useState("");
  const [problem, setProblem] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");
  const [pdfState, setPdfState] = useState("idle"); // idle | sending | sent | error
  const [pdfErr, setPdfErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    if (!problem.trim() || problem.trim().length < 10) {
      setErrMsg("Tell me a little more about the problem (at least a sentence).");
      setState("error");
      return;
    }
    if (!email || !email.includes("@")) {
      setErrMsg("We need an email to send the full report and the gap primer.");
      setState("error");
      return;
    }
    setErrMsg("");
    setState("loading");
    try {
      const { data } = await api.post("/diagnose/tension", {
        machine: machine || null,
        fabric: fabric || null,
        thread: thread || null,
        problem,
        email,
      });
      setResult(data);
      setState("done");
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "The diagnostic engine couldn't read that. Try again with a bit more detail.");
      setState("error");
    }
  };

  const reset = () => {
    setResult(null);
    setState("idle");
    setMachine("");
    setFabric("");
    setThread("");
    setProblem("");
    setPdfState("idle");
    setPdfErr("");
  };

  const sendPdf = async () => {
    if (!result?.diagnostic_id) return;
    setPdfState("sending");
    setPdfErr("");
    try {
      await api.post("/diagnose/email-pdf", {
        diagnostic_id: result.diagnostic_id,
        email,
      });
      setPdfState("sent");
    } catch (err) {
      setPdfErr(err?.response?.data?.detail || "We couldn't send the PDF. Please try again.");
      setPdfState("error");
    }
  };

  return (
    <main data-testid="diagnose-page" className="haven-page-shell" style={{ paddingTop: 32 }}>
      <SectionLabel>Free · AI-powered · trained on Daniel's framework</SectionLabel>
      <h1
        className="font-serif mt-3"
        style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.05, color: "var(--ink)", letterSpacing: "-0.01em" }}
      >
        Diagnose your tension problem.
      </h1>
      <p className="mt-4" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7, maxWidth: 640 }}>
        Describe what your stitch is doing — loops on the back, puckering, shredding, skipping — and the AI returns a
        diagnostic in Daniel's voice: likely cause, the first thing to check, and where to look next. We send the report
        plus the 5-email gap primer to your inbox.
      </p>

      {!result && (
        <form onSubmit={submit} data-testid="diagnose-form" className="mt-8 grid gap-4 max-w-3xl">
          <div className="grid sm:grid-cols-3 gap-3">
            <input
              data-testid="diagnose-machine"
              className="haven-input"
              placeholder="Machine (e.g. Janome 6600)"
              value={machine}
              onChange={(e) => setMachine(e.target.value)}
            />
            <input
              data-testid="diagnose-fabric"
              className="haven-input"
              placeholder="Fabric (e.g. quilting cotton)"
              value={fabric}
              onChange={(e) => setFabric(e.target.value)}
            />
            <input
              data-testid="diagnose-thread"
              className="haven-input"
              placeholder="Thread (e.g. 50wt cotton)"
              value={thread}
              onChange={(e) => setThread(e.target.value)}
            />
          </div>
          <textarea
            data-testid="diagnose-problem"
            className="haven-textarea"
            rows={5}
            placeholder="What is the stitch doing? Where does it happen? What have you already tried?"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            required
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              data-testid="diagnose-email"
              className="haven-input"
              type="email"
              placeholder="Your email — we send the full report"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button
              data-testid="diagnose-submit"
              type="submit"
              className="haven-btn haven-btn-primary"
              disabled={state === "loading"}
            >
              {state === "loading" ? "Diagnosing…" : "Get my diagnosis"}
            </button>
          </div>
          {state === "error" && (
            <div data-testid="diagnose-error" style={{ color: "var(--rose)", fontSize: 12 }}>
              {errMsg}
            </div>
          )}
        </form>
      )}

      {result && (
        <div data-testid="diagnose-result" className="mt-8 grid gap-4 max-w-3xl">
          <div className="haven-card" style={{ padding: 22 }}>
            <SectionLabel>Diagnosis</SectionLabel>
            <p className="font-serif mt-2" style={{ fontSize: 20, lineHeight: 1.4, color: "var(--ink)" }}>
              {result.diagnosis}
            </p>
          </div>
          <div className="haven-card haven-card-gold" style={{ padding: 22 }}>
            <SectionLabel>Likely cause</SectionLabel>
            <p style={{ marginTop: 6, color: "var(--ink)", fontSize: 15, lineHeight: 1.6 }}>{result.likely_cause}</p>
          </div>
          <div className="haven-card" style={{ padding: 22 }}>
            <SectionLabel>First thing to check</SectionLabel>
            <p style={{ marginTop: 6, color: "var(--ink)", fontSize: 15, lineHeight: 1.6 }}>{result.first_check}</p>
            <div className="haven-divider-grain" style={{ margin: "16px 0" }} />
            <SectionLabel>If that's fine, then</SectionLabel>
            <ol className="mt-3 space-y-2" style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.6 }}>
              {(result.deeper_path || []).map((s, i) => (
                <li key={i} style={{ display: "flex", gap: 10 }}>
                  <span style={{ color: "var(--gold)", fontVariantNumeric: "tabular-nums", minWidth: 18 }}>{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="haven-card" style={{ padding: 22, background: "var(--ink)", color: "var(--warm-white)", borderColor: "var(--ink)" }}>
            <SectionLabel>Daniel's note</SectionLabel>
            <p className="font-serif italic" style={{ marginTop: 8, fontSize: 18, lineHeight: 1.5 }}>
              "{result.daniel_note}"
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/join" className="haven-btn haven-btn-gold" data-testid="diagnose-cta-join">
                Lock founding rate
              </Link>
              <button onClick={reset} className="haven-btn haven-btn-ghost" data-testid="diagnose-reset" style={{ background: "transparent", color: "var(--warm-white)", borderColor: "rgba(251,248,242,0.3)" }}>
                Diagnose another
              </button>
            </div>
          </div>

          {/* PDF email upgrade — peak-intent moment */}
          <div
            data-testid="diagnose-pdf-card"
            className="haven-card haven-card-gold"
            style={{ padding: 20 }}
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div style={{ minWidth: 240, flex: 1 }}>
                <SectionLabel>Save it forever</SectionLabel>
                <h3 className="font-serif mt-1" style={{ fontSize: 22, lineHeight: 1.2, color: "var(--ink)" }}>
                  Email me this diagnosis as a PDF
                </h3>
                <p style={{ marginTop: 4, color: "var(--ink-soft)", fontSize: 13, lineHeight: 1.55 }}>
                  Print it. Tape it to the wall above your machine. We'll send it to <b style={{ color: "var(--ink)" }}>{email}</b>.
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {pdfState === "sent" ? (
                  <span data-testid="diagnose-pdf-sent" className="haven-bubble" style={{ background: "var(--sage-light)", color: "var(--ink)", fontSize: 13 }}>
                    Sent · check your inbox
                  </span>
                ) : (
                  <button
                    data-testid="diagnose-send-pdf"
                    onClick={sendPdf}
                    className="haven-btn haven-btn-primary"
                    disabled={pdfState === "sending"}
                  >
                    {pdfState === "sending" ? "Sending PDF…" : "Email me the PDF"}
                  </button>
                )}
                {pdfState === "error" && (
                  <span data-testid="diagnose-pdf-error" style={{ color: "var(--rose)", fontSize: 11 }}>
                    {pdfErr}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
